/* Why this person is learning French, and how much of it they already have.

   Five questions, four of which only exist because of another answer. The card
   asks exactly the ones the answers so far have reached — the same tree the
   public form walks and the server enforces (backend/french_intake.py) — so a
   target level cannot be shown to someone who is not sitting an exam, and a
   staff member reading the card sees the questions that were actually put.

   Rendered only for a French programme, or for any record that carries an
   answer: both person pages decide that, because a record whose programme was
   changed afterwards still has answers worth reading. */

import { Languages, ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EditableField } from '@/components/admin/ProfileField';
import {
  FRENCH_GOAL_OPTIONS,
  FRENCH_TARGET_LEVEL_OPTIONS,
  FRENCH_CURRENT_LEVEL_OPTIONS,
  FRENCH_TEST_PROOF_OPTIONS,
  FRENCH_PLACEMENT_OPTIONS,
  frenchLabel,
  frenchQuestionsAsked,
  wantsFrenchPlacementTest,
  claimsFrenchTestResult,
} from '@/lib/frenchIntake';
import {
  SelectControl,
  sectionCardClass,
  sectionTitleClass,
  sectionTitleStyle,
} from './controls';

const QUESTIONS = [
  { field: 'frenchGoal', label: 'Goal for learning French', options: FRENCH_GOAL_OPTIONS },
  { field: 'frenchTargetLevel', label: 'Target French level', options: FRENCH_TARGET_LEVEL_OPTIONS },
  { field: 'frenchCurrentLevel', label: 'Current French level', options: FRENCH_CURRENT_LEVEL_OPTIONS },
  { field: 'frenchTestProof', label: 'Has a TCF/TEF result', options: FRENCH_TEST_PROOF_OPTIONS },
  { field: 'frenchPlacementPreference', label: 'Starting point', options: FRENCH_PLACEMENT_OPTIONS },
];

export default function FrenchProgramSection({
  person,
  form,
  editing = false,
  setField,
  /* Whether a TCF/TEF result is actually attached. The answer is a claim about a
     document, so saying "yes" and attaching nothing is worth showing as the
     discrepancy it is rather than reading as proof. */
  hasTestResultDocument = false,
  title = 'French Programme',
}) {
  const view = person || {};
  const draft = form || view;
  // Read-only shows the questions the saved answers reached; editing follows the
  // draft, so a question appears the moment the answer above it is given.
  const asked = frenchQuestionsAsked(editing ? draft : view);

  return (
    <Card className={sectionCardClass}>
      <CardHeader className="pb-3">
        <CardTitle className={sectionTitleClass} style={sectionTitleStyle}>
          <Languages className="w-4 h-4 text-[#D4AF37]" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing && (
          <p className="text-xs text-[#062B1A]/60">
            Changing an answer clears the questions below it, here as on the application form.
            Saving updates this person's linked student and admissions profiles.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {QUESTIONS.filter(({ field }) => asked[field]).map(({ field, label, options }) => (
            <EditableField
              key={field}
              label={label}
              editing={editing}
              display={frenchLabel(field, view[field])}
            >
              <SelectControl
                value={draft[field] || ''}
                onChange={(value) => setField?.(field, value)}
                options={options}
                placeholder="Not answered"
                ariaLabel={label}
              />
            </EditableField>
          ))}
        </div>

        {/* The two answers that are a job for somebody, rather than a fact to
            file: a result to go and read, or a test to go and send. */}
        {claimsFrenchTestResult(view) && (
          <p className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${hasTestResultDocument
            ? 'border-[rgba(6,43,26,0.12)] bg-[rgba(6,43,26,0.03)] text-[#062B1A]/70'
            : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
            <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0" />
            {hasTestResultDocument
              ? 'TCF/TEF result attached — see Declaration & Documents.'
              : 'They said they hold a TCF/TEF result, but no result is attached.'}
          </p>
        )}
        {wantsFrenchPlacementTest(view) && (
          <p className="flex items-start gap-2 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-3 text-sm text-[#7A5A05]">
            <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0" />
            They asked to sit a placement test instead of starting at the beginning. Send the
            French placement test from the Language Tests section.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
